import { VgApiResult, vgApiRequest } from "./vg-api.service";

export type OrganisationDetail = {
  orgId: number;
  orgName: string;
  monetaryCurrency?: number;
  latitude?: number;
  longitude?: number;
  /** The address at the pin, as one line; empty until the provider sets its location. */
  address?: string;
  /** Core's country number (see constants/countries). */
  country?: number;
  createdOn_UTC?: string;
  creatorId?: number;
  applicationGUID?: string;
  subscriptionDefinitionId?: number;
  businessType?: number;
  isClaimed?: number;
  metadataOptionList?: Array<{ metaDataIdentifier: string; metaData: unknown; type: number }>;
};

function pickOrg(data: Record<string, unknown> | null | undefined): Partial<OrganisationDetail> {
  if (!data) return {};
  return {
    orgId: Number(data.orgId ?? data.OrgId ?? 0),
    orgName: String(data.orgName ?? data.OrgName ?? ""),
    monetaryCurrency:
      data.monetaryCurrency != null ? Number(data.monetaryCurrency) : undefined,
    latitude: data.latitude != null ? Number(data.latitude) : undefined,
    longitude: data.longitude != null ? Number(data.longitude) : undefined,
    address: data.address != null ? String(data.address) : undefined,
    country: data.country != null ? Number(data.country) : undefined,
    createdOn_UTC: data.createdOn_UTC != null ? String(data.createdOn_UTC) : undefined,
    creatorId: data.creatorId != null ? Number(data.creatorId) : undefined,
    applicationGUID: data.applicationGUID != null ? String(data.applicationGUID) : undefined,
    subscriptionDefinitionId:
      data.subscriptionDefinitionId != null ? Number(data.subscriptionDefinitionId) : undefined,
    businessType: data.businessType != null ? Number(data.businessType) : undefined,
    isClaimed: data.isClaimed != null ? Number(data.isClaimed) : undefined,
    metadataOptionList: Array.isArray(data.metadataOptionList)
      ? (data.metadataOptionList as Array<{ metaDataIdentifier: string; metaData: unknown; type: number }>)
      : undefined,
  };
}

export async function createOrganisation(
  accessToken: string,
  payload: { orgName: string; monetaryCurrency?: number; creatorId: number }
): Promise<VgApiResult<{ newRecordID?: number; NewRecordID?: number }>> {
  return vgApiRequest(accessToken, "Create_Organisation", {
    orgName: payload.orgName,
    monetaryCurrency: payload.monetaryCurrency ?? 978,
    creatorId: payload.creatorId,
    applicationGUID: "",
    latitude: 0,
    longitude: 0,
    isClaimed: 3,    // ENUM_Is_Claimed.Claimed
    businessType: 2, // Enum_Business_Type.Default
  });
}

export async function readOrganisation(
  accessToken: string,
  orgId: number
): Promise<VgApiResult<OrganisationDetail>> {
  const { data, error } = await vgApiRequest<Record<string, unknown>>(accessToken, "ReadOrganisationComplete", {
    orgId,
  });
  if (error) return { data: null, error };
  const p = pickOrg(data);
  return {
    data: {
      orgId: p.orgId ?? orgId,
      orgName: p.orgName ?? "",
      monetaryCurrency: p.monetaryCurrency,
      latitude: p.latitude,
      longitude: p.longitude,
      address: p.address,
      country: p.country,
      createdOn_UTC: p.createdOn_UTC,
      creatorId: p.creatorId,
      applicationGUID: p.applicationGUID,
      subscriptionDefinitionId: p.subscriptionDefinitionId,
      businessType: p.businessType,
      isClaimed: p.isClaimed,
      metadataOptionList: p.metadataOptionList,
    },
    error: null,
  };
}

export async function updateOrganisation(
  accessToken: string,
  payload: OrganisationDetail
): Promise<VgApiResult<unknown>> {
  return vgApiRequest(accessToken, "Update_Organisation", {
    orgId: payload.orgId,
    orgName: payload.orgName,
    monetaryCurrency: payload.monetaryCurrency ?? 978,
    createdOn_UTC: payload.createdOn_UTC ?? "",
    creatorId: payload.creatorId ?? 0,
    applicationGUID: payload.applicationGUID ?? "",
    latitude: payload.latitude ?? 0,
    longitude: payload.longitude ?? 0,
    subscriptionDefinitionId: payload.subscriptionDefinitionId ?? 0,
    businessType: (!payload.businessType || payload.businessType === 1) ? 2 : payload.businessType,
    isClaimed: (!payload.isClaimed || payload.isClaimed === 1) ? 2 : payload.isClaimed,
    metadataOptionList: payload.metadataOptionList ?? [],
  });
}

/**
 * Moves a provider: its pin (what the marketplace search finds it by), the address there,
 * and its country. Leave country as UNKNOWN_COUNTRY for the country the pin is in.
 */
export async function updateOrganisationLocation(
  accessToken: string,
  payload: { orgId: number; latitude: number; longitude: number; address: string; country: number }
): Promise<VgApiResult<unknown>> {
  return vgApiRequest(accessToken, "Update_Organisation_Location", payload);
}

export async function deleteOrganisation(
  accessToken: string,
  orgId: number
): Promise<VgApiResult<unknown>> {
  return vgApiRequest(accessToken, "Delete_Organisation", { orgId });
}
